import { useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import type { ExportData, ExportOptions } from '@/types/export';

export const useExportData = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { user } = useAuth();

  const fetchAllData = async (options: ExportOptions): Promise<ExportData> => {
    if (!user) throw new Error('Usuário não autenticado');

    const data: ExportData = {
      transacoes: [],
      categorias: [],
      contas: [],
      metas: [],
      tags: [],
      transferencias: [],
      faturas_cartao: [],
      compras_parceladas: [],
      transacao_tags: [],
      profile: null
    };

    // Buscar transações com informações de categoria e conta
    if (options.includeTransactions) {
      let query = supabase
        .from('transacoes')
        .select(`
          *,
          categorias(nome),
          contas(nome)
        `)
        .eq('user_id', user.id);

      if (options.dateRange) {
        query = query
          .gte('data', options.dateRange.startDate)
          .lte('data', options.dateRange.endDate);
      }

      const { data: transacoes, error } = await query;
      if (error) throw error;
      
      // Buscar tags das transações
      const transactionIds = (transacoes || []).map(t => t.id);
      let transactionTags: Record<string, unknown>[] = [];
      
      if (transactionIds.length > 0) {
        const { data: tagData, error: tagError } = await supabase
          .from('transacao_tags')
          .select(`
            transacao_id,
            tags(nome)
          `)
          .in('transacao_id', transactionIds);
        
        if (!tagError) {
          transactionTags = tagData || [];
        }
      }
      
      // Processar transações para incluir nomes de categoria, conta e tags
      const processedTransactions = (transacoes || []).map(transaction => {
        const transactionTagsForThis = transactionTags
          .filter(tt => tt.transacao_id === transaction.id)
          .map(tt => tt.tags?.nome)
          .filter(Boolean)
          .join(', ');
          
        return {
          ...transaction,
          categoria_nome: transaction.categorias?.nome || '',
          conta_nome: transaction.contas?.nome || '',
          tags: transactionTagsForThis
        };
      });
      
      data.transacoes = processedTransactions;
    }

    // Categorias, contas, metas e tags não são mais exportadas individualmente
    // Elas são incluídas apenas como parte das transações quando necessário

    // Buscar transferências com nomes das contas
    if (options.includeTransfers) {
      let query = supabase
        .from('transferencias')
        .select(`
          *,
          conta_origem:contas!transferencias_conta_origem_id_fkey(nome),
          conta_destino:contas!transferencias_conta_destino_id_fkey(nome)
        `)
        .eq('user_id', user.id);

      if (options.dateRange) {
        query = query
          .gte('data', options.dateRange.startDate)
          .lte('data', options.dateRange.endDate);
      }

      const { data: transferencias, error } = await query;
      if (error) throw error;
      
      // Processar transferências para incluir nomes das contas
      const processedTransfers = (transferencias || []).map(transfer => ({
        ...transfer,
        conta_origem_nome: transfer.conta_origem?.nome || '',
        conta_destino_nome: transfer.conta_destino?.nome || ''
      }));
      
      data.transferencias = processedTransfers;
    }

    // Faturas de cartão, compras parceladas e perfil não são mais exportados

    return data;
  };

  // Função para filtrar campos específicos das transações
  const filterTransactionFields = (transactions: Record<string, unknown>[]): Record<string, unknown>[] => {
    return transactions
      .map(transaction => ({
        Date: transaction.data,
        Description: transaction.descricao,
        Value: transaction.tipo === 'saida' ? -transaction.valor : transaction.valor,
        Account: transaction.conta_nome || '',
        Status: transaction.foi_paga ? 'Paid' : 'Pending',
        Category: transaction.categoria_nome || '',
        Subcategory: '', // Campo vazio por enquanto, pode ser implementado futuramente
        Tags: transaction.tags || ''
      }))
      .sort((a, b) => new Date(a.Date as string).getTime() - new Date(b.Date as string).getTime());
  };

  // Função para filtrar campos específicos das transferências
  const filterTransferFields = (transfers: Record<string, unknown>[]): Record<string, unknown>[] => {
    return transfers
      .map(transfer => ({
        Date: transfer.data,
        Description: transfer.descricao || '',
        Value: transfer.valor,
        'Source Account': transfer.conta_origem_nome || '',
        'Destination Account': transfer.conta_destino_nome || ''
      }))
      .sort((a, b) => new Date(a.Date as string).getTime() - new Date(b.Date as string).getTime());
  };

  const convertToCSV = (data: Record<string, unknown>[], tableName: string): string => {
    if (!data || data.length === 0) return '';

    // Para transações e transferências, usar campos limitados
    let processedData = data;
    if (tableName === 'transacoes') {
      processedData = filterTransactionFields(data);
    } else if (tableName === 'transferencias') {
      processedData = filterTransferFields(data);
    }

    const headers = Object.keys(processedData[0]);
    const csvContent = [
      `# ${tableName.toUpperCase()}`,
      headers.join(','),
      ...processedData.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    return csvContent;
  };

  const exportData = async (options: ExportOptions) => {
    try {
      setIsExporting(true);
      
      const data = await fetchAllData(options);
      
      let content: string;
      let filename: string;
      let mimeType: string;

      if (options.format === 'json') {
        // Criar objeto apenas com os campos selecionados
        const processedData: Record<string, unknown> = {};
        
        if (options.includeTransactions && data.transacoes.length > 0) {
          processedData.transacoes = filterTransactionFields(data.transacoes);
        }
        if (options.includeTransfers && data.transferencias.length > 0) {
          processedData.transferencias = filterTransferFields(data.transferencias);
        }
        
        content = JSON.stringify(processedData, null, 2);
        filename = `lumo_export_${format(new Date(), 'yyyy-MM-dd')}.json`;
        mimeType = 'application/json';
      } else if (options.format === 'xlsx') {
        // Excel format
        const workbook = new ExcelJS.Workbook();
        
        if (options.includeTransactions && data.transacoes.length > 0) {
          const filteredTransactions = filterTransactionFields(data.transacoes);
          const worksheet = workbook.addWorksheet('Transações');
          
          // Configurar colunas baseadas na primeira linha de dados
          if (filteredTransactions.length > 0) {
            worksheet.columns = Object.keys(filteredTransactions[0]).map(key => ({
              header: key,
              key: key,
              width: 20 // largura padrão
            }));
            
            // Adicionar linhas
            worksheet.addRows(filteredTransactions);
          }
        }
        
        if (options.includeTransfers && data.transferencias.length > 0) {
          const filteredTransfers = filterTransferFields(data.transferencias);
          const worksheet = workbook.addWorksheet('Transferências');
          
          if (filteredTransfers.length > 0) {
            worksheet.columns = Object.keys(filteredTransfers[0]).map(key => ({
              header: key,
              key: key,
              width: 20
            }));
            
            worksheet.addRows(filteredTransfers);
          }
        }

        const excelBuffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        filename = `lumo_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        
        // Para Excel, criamos o blob diretamente
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Dados exportados com sucesso em formato Excel');

        return data;
      } else {
        // CSV format
        const csvSections: string[] = [];
        
        if (options.includeTransactions && data.transacoes.length > 0) {
          csvSections.push(convertToCSV(data.transacoes, 'transacoes'));
        }
        if (options.includeTransfers && data.transferencias.length > 0) {
          csvSections.push(convertToCSV(data.transferencias, 'transferencias'));
        }

        content = csvSections.join('\n\n');
        filename = `lumo_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        mimeType = 'text/csv';
      }

      // Criar e baixar arquivo
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Dados exportados com sucesso em formato ${options.format.toUpperCase()}`);

      return data;
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      toast.error('Ocorreu um erro ao exportar os dados. Tente novamente.');
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportData,
    isExporting,
    fetchAllData
  };
};
